import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Grid, IconButton, Typography } from '@mui/material';
import { Profile } from '@staffedupusa/shared-components';
import React, { FC } from 'react';

import { useUpdateProfile } from '@features/profile/hooks/useUpdateProfile';

type ApplicantAvailabilityProps = {
  applicantDetails: Profile | null;
  enableEdit?: boolean;
};

type AvailabilityLabel =
  | 'mon_availability'
  | 'tues_availability'
  | 'wed_availability'
  | 'thurs_availability'
  | 'fri_availability'
  | 'sat_availability'
  | 'sun_availability';

const daysOfWeek: { title: string; label: AvailabilityLabel }[] = [
  { title: 'monday', label: 'mon_availability' },
  { title: 'tuesday', label: 'tues_availability' },
  { title: 'wednesday', label: 'wed_availability' },
  { title: 'thursday', label: 'thurs_availability' },
  { title: 'friday', label: 'fri_availability' },
  { title: 'saturday', label: 'sat_availability' },
  { title: 'sunday', label: 'sun_availability' },
];

const ApplicantAvailability: FC<ApplicantAvailabilityProps> = ({
  applicantDetails,
  enableEdit = false,
}) => {
  const { updateProfile } = useUpdateProfile({});

  const isAvailable = (availability: string | null, period: string) => {
    if (!availability) return false;

    const periodList = period.includes(',')
      ? period
        .split(',')
        .map((str) => str.trim())
        .sort()
      : [period.trim()];

    const availabilityList = availability
      .split(',')
      .map((str) => str.trim())
      .sort();

    return periodList.every((p) => availabilityList.includes(p));
  };

  const formUpdatedAvailability = (field: string, period: string) => {
    const order = ['Morning', 'Afternoon', 'Evening'];
    if (!applicantDetails) {
      return;
    }

    const sortedApplicationDetsilsFeild = (applicantDetails as any)
      [field] && (applicantDetails as any)
      [field].split(',')
      .sort()
      .join(',');
    const sortedPeriodFeild = period.split(',').sort().join(',');
    if (
      sortedApplicationDetsilsFeild?.includes(period) ||
      (sortedApplicationDetsilsFeild === sortedPeriodFeild)
    ) {
      if (
        sortedApplicationDetsilsFeild === sortedPeriodFeild &&
        period.includes(',')
      ) {
        updateProfile({
          [field]: null,
        });
      } else {
        updateProfile({
          [field]: Array.from(
            new Set(
              (applicantDetails as any)[field]
                .split(',')
                .filter((p: string) => p !== period),
            ),
          ).join(','),
        });
      }
    } else {
      updateProfile({
        [field]: (applicantDetails as any)[field]
          ? Array.from(
            new Set([
              ...(applicantDetails as any)[field].split(','),
              ...period.split(',').map((p: string) => p.trim()),
            ]),
          )
            .sort((a, b) => {
              const indexA = order.indexOf(a.trim());
              const indexB = order.indexOf(b.trim());
              return indexA - indexB;
            })
            .join(',')
          : period,
      });
    }
  };

  const renderAvailabilityCell = (field: string, period: string) => {
    return (
      <IconButton
        disabled={!enableEdit}
        onClick={() => formUpdatedAvailability(field, period)}
      >
        <CheckCircleIcon
          fontSize="small"
          color={
            isAvailable(
              // @ts-ignore
              applicantDetails[field] ?? null,
              period,
            )
              ? 'primary'
              : undefined
          }
        />
      </IconButton>
    );
  };

  if (!applicantDetails) {
    return null;
  }

  return (
    <Grid
      container
      columnSpacing={2}
      textAlign="center"
      sx={{ overflow: 'auto' }}
    >
      <Grid item xs={2.4}>
        <Typography
          variant="h5"
          color="secondary"
          style={{
            marginBottom: '5px',
            fontWeight: 'bold',
            marginTop: '-3px',
            fontSize: '19px',
          }}
        >
          Availability
        </Typography>
      </Grid>
      <Grid item xs={2.4}>
        <Typography color="text.secondary" fontSize="14px">
          Morning
        </Typography>
      </Grid>
      <Grid item xs={2.4}>
        <Typography color="text.secondary" fontSize="14px">
          Afternoon
        </Typography>
      </Grid>
      <Grid item xs={2.4}>
        <Typography color="text.secondary" fontSize="14px">
          Evening
        </Typography>
      </Grid>
      <Grid item xs={2.4}>
        <Typography color="text.secondary" fontSize="14px">
          All day
        </Typography>
      </Grid>
      {daysOfWeek.map((day) => (
        <React.Fragment key={day.title}>
          <Grid item xs={2.4}>
            <Typography
              color="text.secondary"
              textAlign="left"
              textTransform="capitalize"
              fontSize="14px"
            >
              {day.title}
            </Typography>
          </Grid>
          <Grid item xs={2.4}>
            {renderAvailabilityCell(day.label, 'Morning')}
          </Grid>
          <Grid item xs={2.4}>
            {renderAvailabilityCell(day.label, 'Afternoon')}
          </Grid>
          <Grid item xs={2.4}>
            {renderAvailabilityCell(day.label, 'Evenening')}
          </Grid>
          <Grid item xs={2.4}>
            {renderAvailabilityCell(day.label, 'Morning,Afternoon,Evenening')}
          </Grid>
        </React.Fragment>
      ))}
    </Grid>
  );
};

export default ApplicantAvailability;
